import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Chip, Tooltip, makeStyles } from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import TimerOffIcon from "@material-ui/icons/TimerOff";

import api from "../../services/api";
import openSocket, { SOCKET_RECOVER_EVENT } from "../../services/socket-io";
import { i18n } from "../../translate/i18n";

const WINDOW_MS = 24 * 60 * 60 * 1000;

const useStyles = makeStyles(theme => ({
  chipOpen: {
    backgroundColor: "#e8f5e9",
    color: "#1b5e20",
    fontWeight: 600,
  },
  chipWarn: {
    backgroundColor: "#fff8e1",
    color: "#e65100",
    fontWeight: 600,
  },
  chipClosed: {
    backgroundColor: "#ffebee",
    color: "#b71c1c",
    fontWeight: 600,
  },
  chipIdle: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}));

const formatRemaining = ms => {
  if (ms <= 0) return "0h 0m";
  const totalMin = Math.floor(ms / 60000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remH = hours % 24;
    return `${days}d ${remH}h ${minutes}m`;
  }
  return `${hours}h ${minutes}m`;
};

const TicketResponseWindow = ({ ticketId }) => {
  const classes = useStyles();
  const [lastCustomerAt, setLastCustomerAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  const applyInboundAt = useCallback(isoOrDate => {
    if (!isoOrDate) {
      setLastCustomerAt(null);
      return;
    }
    const ts = new Date(isoOrDate).getTime();
    if (!Number.isFinite(ts)) return;
    setLastCustomerAt(prev => {
      if (prev && ts < prev) return prev;
      return ts;
    });
  }, []);

  const refreshFromApi = useCallback(async () => {
    if (!ticketId) return;
    try {
      const { data } = await api.get(
        `/tickets/${ticketId}/response-window`
      );
      applyInboundAt(data.lastCustomerMessageAt);
    } catch (err) {
      // silencioso — não atrapalhar o chat
    }
  }, [ticketId, applyInboundAt]);

  useEffect(() => {
    setLastCustomerAt(null);
    refreshFromApi();
  }, [ticketId, refreshFromApi]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const socket = openSocket();
    if (!socket || !ticketId) return undefined;

    const currentTicketId = String(ticketId);

    const handleConnect = () => {
      socket.emit("joinChatBox", currentTicketId);
    };

    const handleRecover = () => {
      handleConnect();
    };

    const handleAppMessage = data => {
      if (String(data?.message?.ticketId) !== currentTicketId) return;
      if (data.action !== "create") return;
      if (data.message?.fromMe) return;
      applyInboundAt(data.message.createdAt || Date.now());
    };

    if (socket.connected) {
      handleConnect();
    }
    socket.on("connect", handleConnect);
    socket.on("appMessage", handleAppMessage);
    window.addEventListener(SOCKET_RECOVER_EVENT, handleRecover);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("appMessage", handleAppMessage);
      window.removeEventListener(SOCKET_RECOVER_EVENT, handleRecover);
    };
  }, [ticketId, applyInboundAt]);

  const state = useMemo(() => {
    if (!lastCustomerAt) {
      return {
        kind: "idle",
        label: i18n.t("tickets.responseWindow.noCustomerMessage"),
        tip: i18n.t("tickets.responseWindow.noCustomerMessageTip"),
        className: classes.chipIdle,
        icon: <AccessTimeIcon />,
      };
    }

    const endsAt = lastCustomerAt + WINDOW_MS;
    const remainingMs = endsAt - now;

    if (remainingMs <= 0) {
      return {
        kind: "closed",
        label: i18n.t("tickets.responseWindow.closed"),
        tip: i18n.t("tickets.responseWindow.closedTip"),
        className: classes.chipClosed,
        icon: <TimerOffIcon />,
      };
    }

    const warn = remainingMs <= 2 * 60 * 60 * 1000;
    return {
      kind: warn ? "warn" : "open",
      label: i18n.t("tickets.responseWindow.open", {
        time: formatRemaining(remainingMs),
      }),
      tip: i18n.t("tickets.responseWindow.openTip"),
      className: warn ? classes.chipWarn : classes.chipOpen,
      icon: <AccessTimeIcon />,
    };
  }, [lastCustomerAt, now, classes]);

  return (
    <Tooltip title={state.tip} arrow>
      <Chip
        size="small"
        icon={state.icon}
        label={state.label}
        className={state.className}
      />
    </Tooltip>
  );
};

export default TicketResponseWindow;
